/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * Een fout uit het backend aan de gebruiker tonen: de ng21-tegenhanger van de AngularJS-service
 * `erroralert` (golf a3). De sleutels blijven dezelfde — `ERROR.<code>` uit het backendantwoord, met
 * `ERROR.MISC` als de code ontbreekt of niet vertaald is.
 *
 * Injecteert alleen de modalservice en de vertalingen, zodat elke service hem kan gebruiken zonder
 * cyclus.
 */
import {inject, Injectable} from '@angular/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {TranslateService} from '@ngx-translate/core';
import {Sihwlog} from 'sihw-ngx';
import {DlFoutmeldingComponent} from '../components/dialogs/dl-foutmelding/dl-foutmelding.component';

/**
 * Wat het backend bij een afwijzing meestuurt. Alleen `code` wordt gebruikt; de rest gaat naar de
 * console.
 */
export interface Backendfout {
    code?: string;
    message?: string;

    [veld: string]: unknown;
}

@Injectable({providedIn: 'root'})
export class FoutmeldingService {

    private readonly log = new Sihwlog('warn', 'FoutmeldingService');

    private readonly modalService = inject(BsModalService);

    private readonly translate = inject(TranslateService);

    /**
     * Toont de fout als modal en resolvet zodra de gebruiker hem heeft weggeklikt.
     */
    public toon(fout: Backendfout | null | undefined): Promise<void> {
        this.log.warn('Er is een fout opgetreden', fout);
        return this.toonTekst(this.vertaal(fout));
    }

    /**
     * Toont een al vertaalde tekst als foutmelding.
     */
    public async toonTekst(melding: string): Promise<void> {
        await this.open(melding, false);
    }

    /**
     * Toont een foutmelding met een ja- en een nee-knop en geeft het antwoord terug. Voor een fout
     * waar de gebruiker iets over te zeggen heeft; zie `DlFoutmeldingComponent`.
     * @param sleutel de vertaalsleutel van de vraag
     */
    public vraag(sleutel: string): Promise<boolean> {
        return this.open(this.translate.instant(sleutel), true);
    }

    /**
     * Opent de modal en resolvet met het antwoord. Zonder toetsenbord: de melding gaat alleen weg
     * met een knop, anders zou een escape de aanroeper met een openstaande promise achterlaten.
     */
    private open(melding: string, vraag: boolean): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            this.modalService.show(DlFoutmeldingComponent, {
                backdrop: 'static',
                keyboard: false,
                animated: true,
                class: 'dl-modal',
                initialState: {melding, vraag, resolve}
            });
        });
    }

    /**
     * De meldingstekst bij een fout. Ontbreekt de vertaling van `ERROR.<code>`, dan valt hij terug
     * op de algemene tekst — de ng1-versie greep daarvoor naar de sleutel `ERROR_MISC`, die niet
     * bestaat, waardoor de gebruiker letterlijk "ERROR_MISC" te zien kreeg.
     */
    private vertaal(fout: Backendfout | null | undefined): string {
        const sleutel = fout?.code ? `ERROR.${fout.code}` : 'ERROR.MISC';
        const melding = this.translate.instant(sleutel);
        if (melding !== sleutel) {
            return melding;
        }
        this.log.warn('Missende vertaling', sleutel);
        return this.translate.instant('ERROR.MISC');
    }
}
